import axios from "axios";
import { API_BASE_URL } from "@/lib/apiBase";

function authHeaders(token) {
  return { Authorization: `Bearer ${token}` };
}

export async function listDmConversations(token, orgId, opts = {}) {
  const res = await axios.get(`${API_BASE_URL}/dm/conversations`, {
    headers: authHeaders(token),
    params: {
      org_id: orgId,
      unread_only: opts.unreadOnly ? "1" : undefined,
    },
  });
  return res.data || [];
}

export async function getOrCreateDm(token, orgId, peerUserId) {
  const res = await axios.post(
    `${API_BASE_URL}/dm/conversations`,
    { peer_user_id: peerUserId },
    {
      headers: authHeaders(token),
      params: { org_id: orgId },
    }
  );
  return res.data;
}

export async function createDmGroup(token, orgId, { title, memberUserIds }) {
  const body = { member_user_ids: memberUserIds };
  if (title != null && String(title).trim()) {
    body.title = String(title).trim();
  }
  const res = await axios.post(
    `${API_BASE_URL}/dm/conversations/groups`,
    body,
    {
      headers: authHeaders(token),
      params: { org_id: orgId },
    }
  );
  return res.data;
}

export async function updateDmGroup(token, orgId, conversationId, { title }) {
  const res = await axios.patch(
    `${API_BASE_URL}/dm/conversations/${conversationId}`,
    { title },
    {
      headers: authHeaders(token),
      params: { org_id: orgId },
    }
  );
  return res.data;
}

export async function addDmGroupMembers(token, orgId, conversationId, userIds) {
  const res = await axios.post(
    `${API_BASE_URL}/dm/conversations/${conversationId}/members`,
    { user_ids: userIds },
    {
      headers: authHeaders(token),
      params: { org_id: orgId },
    }
  );
  return res.data;
}

export async function removeDmGroupMember(
  token,
  orgId,
  conversationId,
  targetUserId
) {
  const res = await axios.delete(
    `${API_BASE_URL}/dm/conversations/${conversationId}/members/${encodeURIComponent(
      targetUserId
    )}`,
    {
      headers: authHeaders(token),
      params: { org_id: orgId },
    }
  );
  return res.data;
}

export async function ensureDmNotes(token, orgId) {
  const res = await axios.post(
    `${API_BASE_URL}/dm/conversations/notes`,
    {},
    {
      headers: authHeaders(token),
      params: { org_id: orgId },
    }
  );
  return res.data;
}

export async function listDmMessages(token, orgId, conversationId, opts = {}) {
  const res = await axios.get(
    `${API_BASE_URL}/dm/conversations/${conversationId}/messages`,
    {
      headers: authHeaders(token),
      params: {
        org_id: orgId,
        before_id: opts.beforeId,
        limit: opts.limit,
      },
    }
  );
  const data = res.data;
  if (Array.isArray(data)) {
    return { messages: data, preview: false };
  }
  return {
    messages: Array.isArray(data?.messages) ? data.messages : [],
    preview: !!data?.preview,
    peer_last_read_at: data?.peer_last_read_at ?? null,
  };
}

export async function ensureChannelDefaults(token, orgId) {
  const res = await axios.post(
    `${API_BASE_URL}/dm/channels/ensure-defaults`,
    {},
    {
      headers: authHeaders(token),
      params: { org_id: orgId },
    }
  );
  return res.data;
}

export async function browseChannels(token, orgId) {
  const res = await axios.get(`${API_BASE_URL}/dm/channels/browse`, {
    headers: authHeaders(token),
    params: { org_id: orgId },
  });
  return Array.isArray(res.data) ? res.data : [];
}

export async function getChannel(token, orgId, conversationId) {
  const res = await axios.get(
    `${API_BASE_URL}/dm/channels/${conversationId}`,
    {
      headers: authHeaders(token),
      params: { org_id: orgId },
    }
  );
  return res.data;
}

export async function listMyChannels(token, orgId) {
  const res = await axios.get(`${API_BASE_URL}/dm/channels`, {
    headers: authHeaders(token),
    params: { org_id: orgId },
  });
  return Array.isArray(res.data) ? res.data : [];
}

export async function listTeamChannels(token, orgId, teamId) {
  const res = await axios.get(
    `${API_BASE_URL}/dm/teams/${teamId}/channels`,
    {
      headers: authHeaders(token),
      params: { org_id: orgId },
    }
  );
  return Array.isArray(res.data) ? res.data : [];
}

export async function createTeamChannel(token, orgId, teamId, body) {
  const res = await axios.post(
    `${API_BASE_URL}/dm/teams/${teamId}/channels`,
    body,
    {
      headers: authHeaders(token),
      params: { org_id: orgId },
    }
  );
  return res.data;
}

export async function updateChannel(
  token,
  orgId,
  conversationId,
  { title, posting_policy, allowed_poster_ids }
) {
  const body = {};
  if (title !== undefined) body.title = title;
  if (posting_policy !== undefined) body.posting_policy = posting_policy;
  if (allowed_poster_ids !== undefined) {
    body.allowed_poster_ids = allowed_poster_ids;
  }
  const res = await axios.patch(
    `${API_BASE_URL}/dm/channels/${conversationId}`,
    body,
    {
      headers: authHeaders(token),
      params: { org_id: orgId },
    }
  );
  return res.data;
}

export async function deleteChannel(token, orgId, conversationId) {
  const res = await axios.delete(
    `${API_BASE_URL}/dm/channels/${conversationId}`,
    {
      headers: authHeaders(token),
      params: { org_id: orgId },
    }
  );
  return res.data;
}

export async function joinChannel(token, orgId, conversationId) {
  const res = await axios.post(
    `${API_BASE_URL}/dm/channels/${conversationId}/join`,
    {},
    {
      headers: authHeaders(token),
      params: { org_id: orgId },
    }
  );
  return res.data;
}

export async function inviteToChannel(token, orgId, conversationId, userId) {
  const res = await axios.post(
    `${API_BASE_URL}/dm/channels/${conversationId}/invite`,
    { user_id: userId },
    {
      headers: authHeaders(token),
      params: { org_id: orgId },
    }
  );
  return res.data;
}

export async function removeChannelMember(
  token,
  orgId,
  conversationId,
  userId
) {
  const res = await axios.delete(
    `${API_BASE_URL}/dm/channels/${conversationId}/members/${encodeURIComponent(
      userId
    )}`,
    {
      headers: authHeaders(token),
      params: { org_id: orgId },
    }
  );
  return res.data;
}

export async function sendDmMessage(token, orgId, conversationId, body) {
  const res = await axios.post(
    `${API_BASE_URL}/dm/conversations/${conversationId}/messages`,
    body,
    {
      headers: authHeaders(token),
      params: { org_id: orgId },
    }
  );
  return res.data;
}

/** Confirm/cancel on a Circatron bot message. */
export async function postCircatronInteractionCallback(
  token,
  orgId,
  { message_id, action_id, conversation_id }
) {
  const res = await axios.post(
    `${API_BASE_URL}/org/${encodeURIComponent(orgId)}/connectors/interactions/callback`,
    { message_id, action_id, conversation_id },
    { headers: authHeaders(token) }
  );
  return res.data;
}

/** Slash commands available in a channel (composer autocomplete). */
export async function listChannelSlashCommands(token, orgId, conversationId) {
  const res = await axios.get(
    `${API_BASE_URL}/org/${encodeURIComponent(orgId)}/connectors/channels/${conversationId}/slash-commands`,
    { headers: authHeaders(token) }
  );
  return res.data;
}

/** Send a workplace DM with local files and/or Team File links. */
export async function sendDmMedia(
  token,
  orgId,
  conversationId,
  { files = [], teamFiles = [], content } = {}
) {
  const form = new FormData();
  if (content) form.append("content", content);
  for (const file of files) {
    form.append("files", file);
  }
  if (teamFiles.length) {
    form.append(
      "team_files",
      JSON.stringify(
        teamFiles.map((t) => ({
          team_id: t.team_id,
          node_id: t.node_id,
        }))
      )
    );
  }
  const res = await axios.post(
    `${API_BASE_URL}/dm/conversations/${conversationId}/messages/media`,
    form,
    {
      headers: authHeaders(token),
      params: { org_id: orgId },
    }
  );
  return res.data;
}

/** Send a workplace DM message with a single image attachment. */
export async function sendDmImage(token, orgId, conversationId, file, content) {
  return sendDmMedia(token, orgId, conversationId, {
    files: file ? [file] : [],
    content,
  });
}

export async function updateDmMessage(
  token,
  orgId,
  conversationId,
  messageId,
  content,
  references
) {
  const body = { content };
  if (references) body.references = references;
  const res = await axios.patch(
    `${API_BASE_URL}/dm/conversations/${conversationId}/messages/${messageId}`,
    body,
    {
      headers: authHeaders(token),
      params: { org_id: orgId },
    }
  );
  return res.data;
}

export async function deleteDmMessage(token, orgId, conversationId, messageId) {
  const res = await axios.delete(
    `${API_BASE_URL}/dm/conversations/${conversationId}/messages/${messageId}`,
    {
      headers: authHeaders(token),
      params: { org_id: orgId },
    }
  );
  return res.data;
}

export async function markDmRead(token, orgId, conversationId) {
  const res = await axios.post(
    `${API_BASE_URL}/dm/conversations/${conversationId}/read`,
    {},
    {
      headers: authHeaders(token),
      params: { org_id: orgId },
    }
  );
  return res.data;
}

export async function markAllDmRead(token, orgId) {
  const res = await axios.post(
    `${API_BASE_URL}/dm/conversations/read-all`,
    {},
    {
      headers: authHeaders(token),
      params: { org_id: orgId },
    }
  );
  return res.data;
}

export async function setDmMuted(token, orgId, conversationId, muted) {
  const res = await axios.patch(
    `${API_BASE_URL}/dm/conversations/${conversationId}/mute`,
    { muted: !!muted },
    {
      headers: authHeaders(token),
      params: { org_id: orgId },
    }
  );
  return res.data;
}

export async function deleteDmConversation(token, orgId, conversationId) {
  const res = await axios.delete(
    `${API_BASE_URL}/dm/conversations/${conversationId}`,
    {
      headers: authHeaders(token),
      params: { org_id: orgId },
    }
  );
  return res.data;
}

export async function getDmUnreadCount(token, orgId) {
  const res = await axios.get(`${API_BASE_URL}/dm/unread-count`, {
    headers: authHeaders(token),
    params: { org_id: orgId },
  });
  return Number(res.data?.count) || 0;
}

export async function toggleDmReaction(
  token,
  orgId,
  conversationId,
  messageId,
  emoji
) {
  const res = await axios.post(
    `${API_BASE_URL}/dm/conversations/${conversationId}/messages/${messageId}/reactions`,
    { emoji },
    {
      headers: authHeaders(token),
      params: { org_id: orgId },
    }
  );
  return res.data;
}

export async function searchGifs(token, orgId, q, opts = {}) {
  const res = await axios.get(`${API_BASE_URL}/dm/gifs/search`, {
    headers: authHeaders(token),
    params: {
      org_id: orgId,
      q,
      limit: opts.limit,
      offset: opts.offset,
    },
  });
  return Array.isArray(res.data) ? res.data : [];
}

export async function trendingGifs(token, orgId, opts = {}) {
  const res = await axios.get(`${API_BASE_URL}/dm/gifs/trending`, {
    headers: authHeaders(token),
    params: {
      org_id: orgId,
      limit: opts.limit,
    },
  });
  return Array.isArray(res.data) ? res.data : [];
}

export const DM_QUICK_REACT_EMOJIS = ["👍", "❤️", "😂", "🎉", "👀"];

export const DM_TOMBSTONE_TTL_MS = 5 * 60 * 1000;
export const DM_EMPTY_IDLE_MS = 5 * 60 * 1000;

export function draftStorageKey(orgId, conversationId) {
  return `dm-draft:${orgId}:${conversationId}`;
}

export function loadDmDraft(orgId, conversationId) {
  try {
    return localStorage.getItem(draftStorageKey(orgId, conversationId)) || "";
  } catch {
    return "";
  }
}

export function saveDmDraft(orgId, conversationId, text) {
  try {
    const key = draftStorageKey(orgId, conversationId);
    if (!text) localStorage.removeItem(key);
    else localStorage.setItem(key, text);
  } catch {
    /* ignore */
  }
}

export function clearDmDraft(orgId, conversationId) {
  try {
    localStorage.removeItem(draftStorageKey(orgId, conversationId));
  } catch {
    /* ignore */
  }
}
